import { FRAME_CORNER_PX, FRAME_EDGE_PX, FrameTier, frameArtUrl, wornFrameTier } from "./titleFrame";
import { commanderTitle } from "./hub/commanderSheet";

/**
 * The worn title frame on the share cards (MP-23; ui/shareCard.ts's end card and ui/records/recordsShare.ts's records
 * card): a navy name plate reading "Commander · <title>" in the painted frame under its tier's crest, drawn on the card's canvas — a 9-slice of
 * the same art the CSS uses (the @2x source, so a 24 px corner on the 540 px card). No frame worn: no plate, and the
 * cards are drawn exactly as before.
 */
export interface TitlePlate {
  readonly tier: FrameTier;
  readonly text: string;
  readonly frame: CanvasImageSource;
  readonly crest: CanvasImageSource | null;
}

function load(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** The worn frame's plate, its art loaded; null with no frame worn (or no art: the card goes without, never blank). */
export async function wornTitlePlate(tier: FrameTier | null = wornFrameTier()): Promise<TitlePlate | null> {
  if (!tier || typeof document === "undefined") return null;
  const [frame, crest] = await Promise.all([load(frameArtUrl(`frame_${tier}@2x.png`)), load(frameArtUrl(`crest_${tier}@2x.png`))]);
  if (!frame) return null;
  const title = commanderTitle();
  return { tier, text: title ? `Commander · ${title}` : "Commander", frame, crest };
}

/** Each tier's crest on a card (px): its size and how far it sits down over the frame's top edge. */
const CREST: Readonly<Record<FrameTier, { w: number; h: number; seat: number }>> = {
  gold: { w: 80, h: 40, seat: 11 }, silver: { w: 60, h: 35, seat: 15 }, bronze: { w: 50, h: 27.5, seat: 14 },
};

/** Plate size on a card (px). */
export const PLATE_H = 34;
const PLATE_W = 320;
/** The art's band sits this far outside the plate (titleFrame.css --tf-inset). */
const OUTSET = 9;

/** Draws `p` centred on `cx`, its top at `y`, onto a share card. */
export function drawTitlePlate(g: CanvasRenderingContext2D, p: TitlePlate, cx: number, y: number): void {
  const x = cx - PLATE_W / 2;
  const bg = g.createLinearGradient(0, y, 0, y + PLATE_H);
  bg.addColorStop(0, "#2b3752"); bg.addColorStop(1, "#151d31");
  g.fillStyle = bg;
  const r = 9;                                                   // a rounded rect by hand: roundRect is Safari 16+, the shell runs iOS 15
  g.beginPath();
  g.moveTo(x + r, y); g.arcTo(x + PLATE_W, y, x + PLATE_W, y + PLATE_H, r); g.arcTo(x + PLATE_W, y + PLATE_H, x, y + PLATE_H, r);
  g.arcTo(x, y + PLATE_H, x, y, r); g.arcTo(x, y, x + PLATE_W, y, r); g.closePath(); g.fill();
  nineSlice(g, p.frame, x - OUTSET, y - OUTSET, PLATE_W + 2 * OUTSET, PLATE_H + 2 * OUTSET);
  if (p.crest) {                                                 // each tier's crest seated on the top edge (v2: every tier crowned)
    const c = CREST[p.tier];
    g.drawImage(p.crest, cx - c.w / 2, y - OUTSET - c.h + c.seat, c.w, c.h);
  }
  g.textAlign = "center"; g.fillStyle = "#ffe9a6"; g.font = "bold 18px 'Fredoka Display', sans-serif";
  g.fillText(p.text, cx, y + PLATE_H / 2 + 6.5, PLATE_W - 40);
}

/** CSS border-image's `round` on a canvas: the corners as they are, each edge tile repeated a whole number of times. */
function nineSlice(g: CanvasRenderingContext2D, img: CanvasImageSource, x: number, y: number, w: number, h: number): void {
  const C = FRAME_CORNER_PX, E = FRAME_EDGE_PX;
  const s = (img as HTMLImageElement).width / (2 * C + E);        // source px per CSS px (2 for the @2x art)
  const sc = C * s, se = E * s, sw = (img as HTMLImageElement).width, sh = (img as HTMLImageElement).height;
  for (const [sx, sy, dx, dy] of [[0, 0, x, y], [sw - sc, 0, x + w - C, y], [0, sh - sc, x, y + h - C], [sw - sc, sh - sc, x + w - C, y + h - C]] as const) g.drawImage(img, sx, sy, sc, sc, dx, dy, C, C);
  const nx = Math.max(1, Math.round((w - 2 * C) / E)), tw = (w - 2 * C) / nx;
  for (let i = 0; i < nx; i++) {
    g.drawImage(img, sc, 0, se, sc, x + C + i * tw, y, tw, C);
    g.drawImage(img, sc, sh - sc, se, sc, x + C + i * tw, y + h - C, tw, C);
  }
  const ny = Math.max(1, Math.round((h - 2 * C) / E)), th = (h - 2 * C) / ny;
  for (let i = 0; i < ny; i++) {
    g.drawImage(img, 0, sc, sc, se, x, y + C + i * th, C, th);
    g.drawImage(img, sw - sc, sc, sc, se, x + w - C, y + C + i * th, C, th);
  }
}
